<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Níveis de Turmas</h5>
      <b-button
        v-if="hasPermission('adicionar_niveis_turmas')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-plus-circle-fill" title="Novo Cadastro"></i>
      </b-button>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <input
        v-model="filters.nivel"
        placeholder="Filtrar por nível"
        class="form-control"
        @input="filtrarNiveis"
      />
      <b-button
        variant="secondary"
        class="d-flex align-items-center"
        @click="limparFiltros"
      >
        <i class="bi bi-funnel" title="Limpar Filtros"></i>
      </b-button>
    </div>

    <!-- Tabela customizada -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="sort('nivel')">
              Nível {{ arrow('nivel') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('ativo')">
              Ativo {{ arrow('ativo') }}
            </button>
          </th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="n in niveis" :key="n.id">
          <td>{{ n.nivel }}</td>
          <td>{{ n.ativo ? 'Sim' : 'Não' }}</td>
          <td class="text-end">
            <i
              v-if="hasPermission('detalhar_niveis_turmas')"
              class="bi bi-eye-fill text-primary mx-2"
              title="Detalhar"
              @click="detalharNivel(n.id)"
            ></i>
            <i
              v-if="hasPermission('alterar_niveis_turmas')"
              class="bi bi-pencil-fill text-warning mx-2"
              title="Editar"
              @click="editarNivel(n.id)"
            ></i>
            <i
              v-if="hasPermission('excluir_niveis_turmas')"
              class="bi bi-trash-fill text-danger mx-2"
              title="Excluir"
              @click="excluirNivel(n.id)"
            ></i>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limit"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @change="alterarLimite"
        />
      </div>
      <div class="d-flex align-items-center">
        <b-pagination
          v-model="page"
          :per-page="limit"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @input="load"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, watch, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next'
import { useSortableTable } from '@/utils/useSortableTable'
import { usePermissions } from '@/composables/usePermissions'

const router = useRouter()
const { proxy } = getCurrentInstance()
const { fetchPermissions, hasPermission } = usePermissions()

// composable de listagem, paginação e ordenação
const {
  items: niveis,
  totalItems,
  page,
  limit,
  filters,
  load,
  sort,
  arrow
} = useSortableTable(
  `/cadastros/niveis-turmas`,
  { key: 'nivel', order: 'asc' }
)

const pageOptions = [10, 15, 20, 25, 50]

const startRow = computed(() => (page.value - 1) * limit.value + 1)
const endRow   = computed(() => Math.min(page.value * limit.value, totalItems.value))

watch(page, (n, o) => n !== o && load())
watch(limit, (n, o) => {
  if (n !== o) {
    page.value = 1
    load()
  }
})

// filtros
function filtrarNiveis() {
  page.value = 1
  load()
}
function limparFiltros() {
  filters.value = { nivel: '' }
  page.value = 1
  load()
}
function alterarLimite() {
  page.value = 1
  load()
}

// navegação e CRUD
function irParaCadastro() {
  router.push('/cadastros/niveis-turmas/novo')
}
function editarNivel(id) {
  router.push(`/cadastros/niveis-turmas/${id}`)
}
function detalharNivel(id) {
  router.push(`/cadastros/niveis-turmas/${id}/detalhar`)
}
async function excluirNivel(id) {
  const ok = await proxy.$confirm('Tem certeza que deseja excluir este nível?', 'Confirmação')
  if (!ok) return
  try {
    await axios.delete(`/cadastros/niveis-turmas/${id}`)
    load()
    proxy.$toast.success('Nível excluído com sucesso!')
  } catch (e) {
    const msg = e.response?.data?.error || 'Erro ao excluir nível.'
    await proxy.$alert(msg, 'Erro')
  }
}

onMounted(async () => {
  await fetchPermissions()
  filters.value = { nivel: '' }
  load()
})
</script>

<style scoped>
:deep(.pagination.pagination-sm .page-link) {
  padding: rem rem;
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}
/* oculta ícone nativo de ordenação */
.bv-table-sort-icon {
  display: none !important;
}
</style>
